import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Settings,
  Trash2,
  AlertTriangle,
  CheckCircle,
  UserPlus,
  Users,
  Shield,
  X,
  Edit3,
  Save,
  Copy,
  KeyRound,
  Download,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrator', color: 'bg-red-100 text-red-700' },
  { value: 'supervisor', label: 'Supervisor', color: 'bg-blue-100 text-blue-700' },
  { value: 'management', label: 'Management', color: 'bg-purple-100 text-purple-700' },
  { value: 'auditor', label: 'Auditor', color: 'bg-amber-100 text-amber-700' },
  { value: 'driver', label: 'Driver', color: 'bg-emerald-100 text-emerald-700' },
];

const getRoleBadge = (role) => {
  const r = ROLE_OPTIONS.find(o => o.value === role);
  return r ? <Badge className={`${r.color} text-xs`}>{r.label}</Badge> : <Badge>{role}</Badge>;
};

export default function SettingsPage() {
  const { isAdmin, isMaster, user: currentUser } = useAuth();

  // Clear data state
  const [clearing, setClearing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [clearResult, setClearResult] = useState(null);

  // Seed test environment state
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // User management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'driver',
    environment_access: 'test',
  });

  // Password reset state
  const [resetTarget, setResetTarget] = useState(null); // { id, email, isSelf }
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingUsers(true);
    try {
      const res = await usersAPI.list();
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleClearData = async () => {
    if (confirmText !== 'CLEAR ALL DATA') return;
    setClearing(true);
    setClearResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/clear-all-data`);
      setClearResult(res.data);
      toast.success('All data cleared successfully');
      setConfirmOpen(false);
      setConfirmText('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.first_name || !userForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreatingUser(true);
    try {
      await usersAPI.create(userForm);
      toast.success(`User ${userForm.email} created`);
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', first_name: '', last_name: '', role: 'driver', environment_access: 'test' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateUser = async (userId) => {
    if (!editingUser) return;
    try {
      await usersAPI.update(userId, editingUser);
      toast.success('User updated');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (userId, email) => {
    if (!window.confirm(`Deactivate user ${email}?`)) return;
    try {
      await usersAPI.deactivate(userId);
      toast.success(`User ${email} deactivated`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const openResetPassword = (user) => {
    setResetTarget({
      id: user.id,
      email: user.email,
      isSelf: currentUser?.id === user.id,
    });
    setResetPassword('');
    setResetConfirm('');
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (resetPassword !== resetConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    setResetting(true);
    try {
      await usersAPI.update(resetTarget.id, { password: resetPassword });
      toast.success(`Password updated for ${resetTarget.email}`);
      setResetTarget(null);
      setResetPassword('');
      setResetConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const [exportingAll, setExportingAll] = useState(false);
  const handleExportAllData = async () => {
    if (!window.confirm('Download a full data export (all collections + all uploaded files)?\n\nThis may take a minute for large databases.')) return;
    try {
      setExportingAll(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('rtms_token') || localStorage.getItem('rtms_token_test');
      const res = await axios.get(`${API_URL}/api/admin/export-all-data`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `rtms_full_export_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExportingAll(false);
    }
  };

  if (isMaster && !isAdmin) {
    return (
      <div data-testid="settings-page" className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            OWNER TOOLS
          </h1>
          <p className="text-sm text-slate-500 mt-1">Restricted operations for the platform owner</p>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Export All Data</h2>
              <p className="text-sm text-slate-600 mt-1">
                Downloads a single <code className="text-xs bg-slate-100 px-1 rounded">.zip</code> containing every
                collection in BSON (mongorestore-ready) plus all uploaded documents from GridFS. Intended for
                one-off handovers to a customer running their own MongoDB instance.
              </p>
              <Button
                onClick={handleExportAllData}
                disabled={exportingAll}
                className="mt-4 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="master-export-all-btn"
              >
                <Download className="w-4 h-4" />
                {exportingAll ? 'Preparing archive…' : 'Export All Data (.zip)'}
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                Restore: <code className="text-xs bg-slate-100 px-1 rounded">mongorestore --uri=&quot;&lt;url&gt;&quot; --nsFrom=&#39;test_database.*&#39; --nsTo=&#39;&lt;your-db&gt;.*&#39; &lt;extracted&gt;</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div data-testid="settings-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            SETTINGS
          </h1>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-8 text-center max-w-md">
          <Settings className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Admin access required for system settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1
          className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Barlow Condensed' }}
          data-testid="settings-title"
        >
          SETTINGS
        </h1>
        <p className="text-sm text-slate-500 mt-1">System administration and configuration</p>
      </div>

      <div className="max-w-4xl space-y-6">

        {/* ─── User Management ─── */}
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                User Management
              </h2>
            </div>
            <Button size="sm" onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
              <UserPlus className="w-4 h-4 mr-1" />
              Create User
            </Button>
          </div>

          {/* Create User Form */}
          {showCreateUser && (
            <div className="p-4 border-b border-slate-200 bg-blue-50/50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">New User</h3>
                <button onClick={() => setShowCreateUser(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={e => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="create-user-first-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={userForm.last_name}
                    onChange={e => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="create-user-last-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="create-user-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Password *</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="create-user-password"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Role *</label>
                  <select
                    value={userForm.role}
                    onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="create-user-role"
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Environment Access</label>
                  <select
                    value={userForm.role === 'admin' ? 'both' : userForm.environment_access}
                    disabled={userForm.role === 'admin'}
                    onChange={e => setUserForm({ ...userForm, environment_access: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-100"
                    data-testid="create-user-env-access"
                  >
                    <option value="live">Live only</option>
                    <option value="test">Test only</option>
                    <option value="both">Both</option>
                  </select>
                  {userForm.role === 'admin' && (
                    <p className="text-xs text-slate-500 mt-1">Admins always have access to both environments</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" onClick={handleCreateUser} disabled={creatingUser} data-testid="create-user-save">
                  {creatingUser ? 'Creating...' : 'Create User'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowCreateUser(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
            {loadingUsers ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No users found</div>
            ) : (
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Name</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Role</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Environment</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {editingUser?.id === user.id ? (
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={editingUser.first_name}
                              onChange={e => setEditingUser({ ...editingUser, first_name: e.target.value })}
                              className="w-20 px-1 py-0.5 border border-slate-300 rounded text-xs"
                            />
                            <input
                              type="text"
                              value={editingUser.last_name}
                              onChange={e => setEditingUser({ ...editingUser, last_name: e.target.value })}
                              className="w-20 px-1 py-0.5 border border-slate-300 rounded text-xs"
                            />
                          </div>
                        ) : (
                          `${user.first_name} ${user.last_name}`
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.email}</td>
                      <td className="px-4 py-3">
                        {editingUser?.id === user.id ? (
                          <select
                            value={editingUser.role}
                            onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                            className="px-1 py-0.5 border border-slate-300 rounded text-xs"
                          >
                            {ROLE_OPTIONS.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        ) : (
                          getRoleBadge(user.role)
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'admin' ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Both (Admin)</Badge>
                        ) : (
                          <select
                            value={user.environment_access || 'test'}
                            onChange={async (e) => {
                              const newAccess = e.target.value;
                              try {
                                await usersAPI.update(user.id, { environment_access: newAccess });
                                toast.success(`Environment access updated`);
                                fetchUsers();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Update failed');
                              }
                            }}
                            className="px-2 py-1 border border-slate-300 rounded text-xs font-medium"
                            data-testid={`env-access-${user.id}`}
                          >
                            <option value="live">Live only</option>
                            <option value="test">Test only</option>
                            <option value="both">Both</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={user.is_active ? 'bg-green-100 text-green-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingUser?.id === user.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleUpdateUser(user.id)}
                              data-testid={`save-user-${user.id}`}
                            >
                              <Save className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setEditingUser({
                                id: user.id,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                role: user.role,
                              })}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="text-amber-600 hover:text-amber-700"
                              title="Reset password"
                              onClick={() => openResetPassword(user)}
                              data-testid={`reset-password-${user.id}`}
                            >
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            {user.is_active && user.email !== 'admin@rtms.com' && (
                              <Button
                                variant="ghost" size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeactivateUser(user.id, user.email)}
                                data-testid={`deactivate-user-${user.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ─── Clear All Data ─── */}
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Data Management
            </h2>
          </div>
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900">Clear All Data</h3>
                <p className="text-sm text-slate-500 mt-1 mb-3">
                  Permanently remove all operational data from the system. This includes vehicles, drivers,
                  inspections, loads, breakdowns, accidents, fines, fuel records, training records,
                  corrective actions, and all uploaded files.
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm mb-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-medium">
                      This will keep: Admin account, User Manual, SOPs, and Form Templates. Everything else will be permanently deleted.
                    </span>
                  </div>
                </div>

                {!confirmOpen ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    data-testid="clear-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear All Data
                  </Button>
                ) : (
                  <div className="space-y-3 p-4 border border-red-200 bg-red-50 rounded-sm">
                    <p className="text-sm font-medium text-red-800">
                      Type <code className="bg-red-100 px-1.5 py-0.5 rounded text-xs font-bold">CLEAR ALL DATA</code> to confirm:
                    </p>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Type CLEAR ALL DATA"
                      className="w-full px-3 py-2 border border-red-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="clear-data-confirm-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleClearData}
                        disabled={confirmText !== 'CLEAR ALL DATA' || clearing}
                        data-testid="clear-data-confirm-btn"
                      >
                        {clearing ? 'Clearing...' : 'Confirm Clear'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
                        data-testid="clear-data-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {clearResult && (
              <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-sm" data-testid="clear-data-result">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">{clearResult.message}</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(clearResult.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs text-slate-600 py-0.5">
                      <span className="font-mono">{key}</span>
                      <span className="text-slate-500">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* ─── Seed Test Environment ─── */}
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Test Environment
            </h2>
          </div>
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
                <Copy className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900">Seed Test Environment</h3>
                <p className="text-sm text-slate-500 mt-1 mb-3">
                  Copy all live data to the test environment. Users can access the test environment at
                  <code className="bg-slate-100 px-1 mx-1 rounded text-xs font-mono">/test/login</code>
                  to practice without affecting live data.
                </p>
                <Button
                  size="sm"
                  onClick={async () => {
                    setSeeding(true);
                    setSeedResult(null);
                    try {
                      const res = await axios.post(`${API_URL}/admin/seed-test-environment`);
                      setSeedResult(res.data);
                      toast.success('Test environment seeded from live data');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to seed test environment');
                    }
                    setSeeding(false);
                  }}
                  disabled={seeding}
                  data-testid="seed-test-btn"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  {seeding ? 'Seeding...' : 'Seed Test Environment from Live'}
                </Button>
                {seedResult && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-800">{seedResult.message}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {Object.keys(seedResult.collections_copied || {}).length} collections copied
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ─── Password Reset Modal ─── */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-sm w-full max-w-md shadow-xl overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
                {resetTarget.isSelf ? 'Change Your Password' : 'Reset User Password'}
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-sm text-slate-600">
                  {resetTarget.isSelf ? 'Updating password for:' : 'Resetting password for:'}
                </p>
                <p className="text-sm font-semibold text-slate-900">{resetTarget.email}</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">New Password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="Minimum 6 characters"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Confirm Password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="Re-type new password"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {!resetTarget.isSelf && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm">
                  <p className="text-xs text-amber-800">
                    The user will need this new password to log in. Make sure to communicate it to them securely.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setResetTarget(null); setResetPassword(''); setResetConfirm(''); }}
                  disabled={resetting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleResetPassword}
                  disabled={resetting || !resetPassword || !resetConfirm}
                  data-testid="reset-password-submit-btn"
                >
                  {resetting ? 'Updating...' : (resetTarget.isSelf ? 'Update Password' : 'Reset Password')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
